import { quantizeSize } from './utils';
export const NATIVE_STICKY_NAME = (() => {
    if (typeof window === 'undefined') {
        return false;
    }
    const node = document.createElement('div');
    const result = ['', '-webkit-', '-moz-', '-ms-'].find((prefix) => {
        try {
            node.style.position = `${prefix}sticky`;
        }
        catch (e) {
            // Do nothing
        }
        return node.style.position !== '';
    });
    if (result == null)
        return false;
    return `${result}sticky`;
})();
export const IS_IOS = (() => {
    if (typeof window === 'undefined') {
        return false;
    }
    return /ios|iphone|ipad/i.test(navigator.userAgent);
})();
export const DEFAULT_STICKY_STATE = {
    disabled: true,
    emulatedDisabled: true,
    bottom: false,
    isAbsolute: false,
    isNativeSticky: false,
    offset: 0,
    emulatedOffset: 0,
    height: null,
    width: null,
    beginY: 0,
};
export const STICKY_OPTIONS_KEYS = [
    'type',
    'shouldInfluenceScroll',
    'direction',
    'offset',
    'offsetRange',
    'disableNativeSticky',
    'disableBottom',
    'disableHeight',
    'disabled',
    'iosWorkaround',
    'alwaysStick',
    'fit',
    'fitHeight',
    'transition',
    'zIndex',
];
export function getStickyContainerStyle(state, options) {
    if (state.disabled) {
        return {};
    }
    if (state.isNativeSticky) {
        return {
            position: NATIVE_STICKY_NAME,
            [options.direction]: `${state.offset}px`,
            transform: 'scaleZ(0px)',
            transition: `${options.transition || 0.1}s ${options.direction}`,
            zIndex: options.zIndex,
        };
    }
    if (state.emulatedDisabled || options.disableHeight) {
        return {};
    }
    return {
        height: `${state.height}px`,
    };
}
export function getStickyChildStyle(state, options) {
    if (state.disabled) {
        return {};
    }
    if (state.isNativeSticky) {
        return {
            position: 'relative',
            height: options.fitHeight ? `${state.height}px` : undefined,
            boxSizing: options.fitHeight ? 'border-box' : undefined,
        };
    }
    if (state.emulatedDisabled) {
        const style = {
            position: 'relative',
            zIndex: options.zIndex,
            height: options.fitHeight ? `${state.height}px` : undefined,
            boxSizing: options.fitHeight ? 'border-box' : undefined,
        };
        return style;
    }
    const style = {
        position: state.isAbsolute || state.bottom ? 'absolute' : 'fixed',
        transform: 'scaleZ(0px)',
        height: options.fitHeight ? `${state.height}px` : undefined,
        boxSizing: options.fitHeight ? 'border-box' : undefined,
        transition: `${options.transition || 0.1}s ${options.direction}`,
        zIndex: options.zIndex,
    };
    if (state.bottom) {
        style[options.direction === 'top' ? 'bottom' : 'top'] = '0px';
    }
    else {
        style[options.direction] = `${state.emulatedOffset}px`;
    }
    if (options.fit) {
        style.left = '0px';
        style.right = '0px';
    }
    else {
        style.width = `${state.width}px`;
    }
    return style;
}
export function getUpdatedStickyState(options, containerElem, childElem) {
    // Check if both elements are available
    if (containerElem == null || childElem == null) {
        return DEFAULT_STICKY_STATE;
    }
    // Check if the container is visible. If it's not shown, there's no need to
    // calculate floating state.
    const containerElemParent = containerElem.parentElement;
    if (containerElem.offsetParent == null || containerElemParent == null) {
        return DEFAULT_STICKY_STATE;
    }
    // If disabled, don't do anything.
    if (options.disabled) {
        return DEFAULT_STICKY_STATE;
    }
    const windowHeight = window.innerHeight;
    let emulatedDisabled;
    let bottom;
    let height = null;
    const childRect = childElem.getBoundingClientRect();
    const containerRect = containerElem.getBoundingClientRect();
    const { top: containerTop, bottom: containerBottom, } = containerRect;
    // Math.round is for avoiding precision issues
    // (we don't need subpixel accuracy)
    const beginY = Math.round(containerTop +
        (window.scrollY || document.documentElement.scrollTop));
    const containerWidth = containerRect.right - containerRect.left;
    if (options.direction === 'bottom') {
        emulatedDisabled = !options.alwaysStick &&
            containerBottom - options.offset < windowHeight;
        bottom = false;
    }
    else {
        // Defaults to top
        const { bottom: parentBottom, } = containerElemParent.getBoundingClientRect();
        emulatedDisabled = !options.alwaysStick &&
            containerTop - options.offset > 0;
        if (options.fitHeight) {
            const bottomDiff = Math.max(0, windowHeight - parentBottom);
            height = quantizeSize(Math.min(parentBottom - containerTop, windowHeight - options.offset - bottomDiff));
            const style = window.getComputedStyle(childElem);
            const minHeight = parseInt(style.minHeight, 10);
            bottom =
                !options.disableBottom &&
                    Math.max(height, minHeight) + options.offset > parentBottom;
        }
        else {
            const childHeight = childRect.bottom - childRect.top;
            bottom =
                !options.disableBottom &&
                    childHeight + options.offset > parentBottom;
        }
    }
    const isIosWorkaround = options.iosWorkaround && IS_IOS;
    const isNative = !isIosWorkaround &&
        !options.disableNativeSticky && NATIVE_STICKY_NAME;
    if (!options.fitHeight) {
        // eslint-disable-next-line prefer-destructuring
        height = quantizeSize(childRect.height);
    }
    if (isNative) {
        return {
            disabled: false,
            bottom,
            emulatedDisabled,
            offset: options.offset,
            emulatedOffset: quantizeSize(options.offset),
            height,
            width: null,
            isAbsolute: false,
            isNativeSticky: true,
            beginY,
        };
    }
    const containerStyle = getComputedStyle(containerElem);
    const width = quantizeSize(containerWidth -
        parseFloat(containerStyle.paddingLeft) -
        parseFloat(containerStyle.paddingRight));
    if (isIosWorkaround) {
        // Run iOS workaround routine. ...
        const offsetDiff = containerElemParent.getBoundingClientRect().top -
            document.body.getBoundingClientRect().top;
        return {
            disabled: false,
            bottom,
            emulatedDisabled,
            offset: options.offset,
            emulatedOffset: quantizeSize(options.offset + window.scrollY - offsetDiff),
            height,
            width,
            isAbsolute: true,
            isNativeSticky: false,
            beginY,
        };
    }
    return {
        disabled: false,
        bottom,
        emulatedDisabled,
        offset: options.offset,
        emulatedOffset: quantizeSize(options.offset),
        height,
        width,
        isAbsolute: false,
        isNativeSticky: false,
        beginY,
    };
}
export function hasStickyStateChanged(prevState, nextState) {
    let key;
    for (key in nextState) {
        if (nextState[key] !== prevState[key]) {
            return true;
        }
    }
    return false;
}
export function assertStickyParentPosition(options, containerElem) {
    // Do nothing in production mode
    if (process.env.NODE_ENV === 'production') {
        return;
    }
    if (options.disableNativeSticky && options.disableBottom) {
        return;
    }
    if (containerElem == null) {
        return;
    }
    // Check if the container is visible. If it's not shown, do nothing.
    const containerElemParent = containerElem.parentElement;
    if (containerElem.offsetParent == null || containerElemParent == null) {
        return;
    }
    // Retrieve style info...
    const styleInfo = window.getComputedStyle(containerElemParent);
    if (!['relative', 'absolute'].includes(styleInfo.position)) {
        throw new Error('Container node\'s parent must be set to ' +
            'position: relative or absolute to ' +
            'emulate native sticky\'s behavior. ' +
            'set disableNativeSticky and disableBottom if you want to avoid ' +
            'native sticky behavior (sticking at the bottom of parent node)');
    }
}
//# sourceMappingURL=stickyEmulation.js.map